import { useCallback, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import ErrorNotice from "../../../errors/ErrorNotice";
import { isProblemWithCode, toUiError, type UiError } from "../../../errors/uiError";
import { useSubmission } from "../../../shared/state/useSubmission";
import UndoNotice from "../../../ui/feedback/UndoNotice";
import ReasonDialog from "./ReasonDialog";

/**
 * One change of who may do what: a group's set, a member, an inclusion or the order. Grants
 * apply at once, with Undo (redesign R3-2 "Saving", PV3-1); Undo is the inverse write, made
 * the same way, so it asks for a reason too when it switches the server's keys on again.
 */
export interface AccessChange {
  /** Says what changed, once it has: "Contributors can now make releases." */
  readonly done: string;
  /** Known beforehand to switch on the server's keys: the reason is asked first (GS-4). */
  readonly needsReason: boolean;
  /** The reason dialog's title, asked first or when the server answers that a reason is needed. */
  readonly reasonTitle: string;
  readonly apply: (reason: string | null) => Promise<void>;
  /** Reverses it; `null` where nothing does, or for an undo itself. */
  readonly inverse: AccessChange | null;
}

export interface AccessChanges {
  readonly run: (change: AccessChange) => void;
  /** A change is being written, or its result not read back yet: every control waits. */
  readonly busy: boolean;
  /** The reason dialog, the Undo notice and a refusal: render once on the screen. */
  readonly feedback: ReactNode;
}

/**
 * Runs changes one at a time. `onChanged` reads the list again after each; `shown` is the list
 * on screen, and nothing else is written until a newer one replaces it, because every change
 * is built from what is shown (a group's whole set, the whole order).
 */
export function useAccessChanges(onChanged: () => void, shown: object): AccessChanges {
  const { t } = useTranslation();
  const { state, submit, reset } = useSubmission();
  const [asking, setAsking] = useState<AccessChange | null>(null);
  const [undoable, setUndoable] = useState<AccessChange | null>(null);
  // The list that was on screen when the last write succeeded; while it still is, the write's result isn't read back.
  const [writtenOver, setWrittenOver] = useState<object | null>(null);
  const waiting = writtenOver !== null && writtenOver === shown;
  const busy = state.status === "submitting" || waiting || asking !== null;

  const applied = useCallback((change: AccessChange) => {
    setWrittenOver(shown);
    onChanged();
    setUndoable(change.inverse === null ? null : change);
  }, [onChanged, shown]);

  const failed = state.status === "failed";
  const run = useCallback((change: AccessChange) => {
    if (busy) return;
    setUndoable(null);
    if (change.needsReason) {
      // The dialog shows its own failure; an earlier one beside the list no longer applies.
      if (failed) reset();
      setAsking(change);
      return;
    }
    submit(async () => {
      try {
        await change.apply(null);
      } catch (refusal) {
        // The server knows best whether the keys come on (an included group's own set): then it asks why.
        if (asksForReason(toUiError(refusal))) {
          setAsking(change);
          return;
        }
        // A refusal can mean the list changed meanwhile (a new order, a member gone): show it as it is now.
        onChanged();
        throw refusal;
      }
      applied(change);
    });
  }, [applied, busy, failed, onChanged, reset, submit]);

  const dismiss = useCallback(() => { setUndoable(null); }, []);
  const runInverse = (inverse: AccessChange) => () => { run(inverse); };

  const feedback = (
    <>
      {state.status === "failed" ? <ErrorNotice error={state.error} compact={true} /> : null}
      {asking === null ? null : (
        <ReasonDialog
          title={asking.reasonTitle}
          onConfirm={async (reason) => { await asking.apply(reason); applied(asking); }}
          onClose={() => { setAsking(null); }}
        />
      )}
      {undoable === null || undoable.inverse === null || waiting ? null : (
        <UndoNotice message={undoable.done} undoLabel={t("access.undo")} onDismiss={dismiss} onUndo={runInverse(undoable.inverse)} />
      )}
    </>
  );
  return { run, busy, feedback };
}

/** The server refused because switching on its keys needs a reason (`check_reason`, GS-4). */
function asksForReason(error: UiError): boolean {
  return isProblemWithCode(error, "invalid_value") && error.kind === "problem" && error.problem.details["rule"] === "server_keys_reason";
}
